import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { data, plural, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { Selo, useAbasQueGrudam } from '../ui';
import type { Tom } from '../ui';
import type { Os } from './OsForms';
import { Icone } from '../Icone';

/**
 * A moldura da OS — o cabeçalho, a régua de etapas e as abas (onda 256).
 *
 * «As 3 interfaces OS, Recebimento e Programação não conversam
 *  visualmente/design entre si» (Humberto, 02/10/2026). A ideia do fluxo
 * da OS (`000-ideia-fluxo-os-recebimento-programacao`, §6 e §9) põe a OS
 * como o lugar do trabalho: cada etapa é uma aba da mesma OS, com o mesmo
 * cabeçalho em cima e uma régua que diz onde se está e o que falta.
 *
 * Esta é a onda 3 daquele plano. A ficha da OS deixa de ser uma página
 * que rola com seis cartões empilhados e passa a ter abas, cada uma com
 * endereço próprio (para o favorito, o voltar do navegador e o link que
 * outra tela manda):
 *
 *   /os/:id                Resumo — as três frentes, o próximo passo, os dados
 *   /os/:id/escopo         Escopo — o que foi contratado e as revisões
 *   /os/:id/amostras       Amostras — a tabela única, do previsto ao descarte
 *   /os/:id/programacao    Programação — a grade amostras × escopo (onda 257;
 *                          o endereço antigo /programar redireciona)
 *   /os/:id/cronograma     Cronograma — previsto × realizado de cada ensaio,
 *                          com os atrasos; só leitura (onda 304, RN-249)
 *   /os/:id/quantitativos  Quantitativos — o que já foi executado
 *   /os/:id/historico      Histórico — situações e trilha de alterações
 *
 * A ordem das abas é a ordem do serviço, e a régua usa os mesmos nomes.
 */

export type AbaDaOs =
  | 'resumo' | 'escopo' | 'amostras' | 'programacao' | 'cronograma'
  | 'quantitativos' | 'historico';

/** O endereço de cada aba. */
export function rotaDaAba(osId: string, aba: AbaDaOs): string {
  switch (aba) {
    case 'resumo': return `/os/${osId}`;
    default: return `/os/${osId}/${aba}`;
  }
}

/** O nome que a aba mostra — e que a trilha repete. */
export const NOME_DA_ABA: Record<AbaDaOs, string> = {
  resumo: 'Resumo',
  escopo: 'Escopo',
  amostras: 'Amostras',
  programacao: 'Programação',
  cronograma: 'Cronograma',
  quantitativos: 'Quantitativos',
  historico: 'Histórico',
};

const TOM_TECNICO: Record<string, Tom> = {
  rascunho: 'neutro', aguardando_aprovacao: 'neutro',
  aguardando_programacao: 'neutro', programada: 'andamento',
  aguardando_amostras: 'atencao', amostras_recebidas: 'andamento',
  ensaios_em_andamento: 'andamento', aguardando_dados: 'atencao',
  em_processamento: 'andamento', com_pendencia: 'critico',
  concluida: 'ok', suspensa: 'atencao', cancelada: 'neutro',
};

/* ── Os números da OS, num lugar só ─────────────────────────────────── */

/**
 * As contas que o cabeçalho, a régua e as abas usam.
 *
 * Saem todas da ficha (`GET /os/:id`) — nenhuma chamada a mais. As três
 * telas precisam dizer o MESMO número (8 recebidas, 2 por chegar), e é por
 * isso que a conta mora aqui e não em cada uma.
 */
export function numerosDaOs(os: Os) {
  const previstas = Number(os.amostras_previstas_total ?? 0);
  const atendidas = Number(os.amostras_previstas_atendidas ?? 0);
  const recebidas = Number(os.amostras_recebidas ?? 0);
  const porChegar = Math.max(previstas - atendidas, 0);
  const metodos = (os.escopo_por_metodo ?? []).length;
  const contratados = Number(os.escopo_resumo?.ensaios_contratados ?? 0);
  const programados = Number(os.escopo_resumo?.ensaios_programados ?? 0);
  /*
   * 332 (D1 da spec «Amostras em lote»): o ensaio marcado em amostra por
   * chegar É programação definida — «a programação fica "0 de 3
   * programados" (OS 2608339). Quando na verdade a programação já foi
   * definida» (Humberto, 07/10/2026). A API conta a marca desde a 0220
   * (`ensaios_reservados`); a régua, o próximo passo, o Escopo e a aba
   * Programação passam a ler a conta toda daqui.
   */
  const reservados = Number(os.escopo_resumo?.ensaios_reservados ?? 0);
  const programadosTodos = programados + reservados;
  const ensaios = Number(os.ensaios_total ?? 0);
  const aprovados = Number(os.ensaios_aprovados ?? 0);
  const emExecucao = Number(os.ensaios_em_execucao ?? 0);
  const devolvidos = Number(os.ensaios_devolvidos ?? 0);
  return {
    previstas, atendidas, recebidas, porChegar, metodos, contratados,
    programados, reservados, programadosTodos, ensaios, aprovados, emExecucao, devolvidos,
    /* Uma linha por amostra (204): a recebida e a que ainda não chegou. */
    amostras: recebidas + porChegar,
  };
}

/** O prazo em palavras — «faltam N d» ou «atrasada há N d» (§9.2). */
export function prazoEmPalavras(os: Os): string | null {
  if (!os.prazo_contratual) return null;
  const dias = os.dias_para_prazo === null || os.dias_para_prazo === undefined
    ? null : Number(os.dias_para_prazo);
  if (dias === null || Number.isNaN(dias)) return null;
  if (dias < 0) return `atrasada há ${-dias} d`;
  if (dias === 0) return 'vence hoje';
  return `faltam ${dias} d`;
}

/* ── O próximo passo — a ação primária do cabeçalho ─────────────────── */

export interface Passo { texto: string; para: string; diz: string }

/**
 * O que a OS pede agora, na ordem do serviço.
 *
 * O cabeçalho tem UMA ação primária, e ela é o próximo passo (§9.1). Quem
 * abre a OS no meio do serviço não precisa procurar o que fazer: o botão
 * azul diz. Quando não há passo óbvio (a OS anda sozinha na bancada), não
 * há botão primário — um botão que sempre está lá deixa de dizer alguma
 * coisa.
 */
export function proximoPasso(os: Os, pode: (p: string) => boolean): Passo | null {
  /* 0288: a arquivada é só leitura — não há próximo passo. */
  if (os.cancelada || os.suspensa || os.arquivada) return null;
  const n = numerosDaOs(os);
  if (n.metodos === 0) {
    if (!pode('os.editar') && !pode('os.revisar')) return null;
    return {
      texto: 'Lançar o escopo', para: `${rotaDaAba(os.id, 'escopo')}?escopo=1`,
      diz: 'Esta OS ainda não tem ensaio no escopo — e é o escopo que libera a programação.',
    };
  }
  if (os.status_tecnico === 'com_pendencia') {
    return {
      texto: 'Ver a fila', para: '/fila',
      diz: 'Há ensaio devolvido para correção.',
    };
  }
  const recebidasSemEnsaio = os.status_tecnico === 'aguardando_programacao' && n.recebidas > 0;
  if (recebidasSemEnsaio && pode('os.programar')) {
    return {
      texto: 'Programar', para: rotaDaAba(os.id, 'programacao'),
      diz: 'Há amostra recebida sem ensaio marcado.',
    };
  }
  if ((n.porChegar > 0 || n.amostras === 0) && pode('amostra.receber')) {
    return {
      texto: 'Receber amostras', para: `/recebimento/novo?os=${os.id}`,
      diz: n.porChegar > 0
        ? `${n.porChegar} amostra(s) por chegar.`
        : 'Nenhuma amostra ainda.',
    };
  }
  if (n.programadosTodos < n.contratados && n.amostras > 0 && pode('os.programar')) {
    return {
      texto: 'Programar', para: rotaDaAba(os.id, 'programacao'),
      diz: `${n.contratados - n.programadosTodos} ensaio(s) do escopo ainda sem amostra.`,
    };
  }
  return null;
}

/* ── O cabeçalho ────────────────────────────────────────────────────── */

/**
 * O cabeçalho da OS — o mesmo em todas as abas.
 *
 * «OS 2610412» em fonte de código, a situação técnica e a revisão como
 * selos, e embaixo cliente · obra · município · prazo. À direita, uma só
 * ação primária (o próximo passo) e as secundárias de sempre. A trilha
 * («Ordens de serviço / OS 2610412 / Escopo») é da moldura, logo acima.
 */
export function CabecalhoDaOs({ os, passo, acoes }: {
  os: Os;
  passo: Passo | null;
  /** As ações secundárias: vão para o menu «⋯» (267). */
  acoes?: ReactNode;
}) {
  const { pode } = useSessao();
  const prazo = prazoEmPalavras(os);
  const lugar = [os.municipio, os.uf].filter(Boolean).join(' — ');
  /*
   * 281 · «Receber amostras» em TODAS as abas da OS («senti falta de algum
   * comando de recebimento de amostras diretamente na aba Ordem de
   * Serviço» — Humberto, 06/10/2026; decidido «nos 2 lugares»). Cheio
   * quando é o próximo passo (vem pelo `passo`); contornado quando não é.
   */
  const receber = pode('amostra.receber') && !os.cancelada && !os.suspensa && !os.arquivada
    && !passo?.para.startsWith('/recebimento/novo');
  return (
    <header className="cabeca-os">
      <div className="cabeca-os-id">
        <div className="cabeca-os-linha">
          <h1>OS <span className="mono">{os.numero}</span></h1>
          {os.cancelada ? <Selo tom="neutro">Cancelada</Selo>
            : os.suspensa ? <Selo tom="atencao">Suspensa</Selo>
              : (
                <Selo tom={TOM_TECNICO[os.status_tecnico] ?? 'neutro'}>
                  {rotulo(os.status_tecnico)}
                </Selo>
              )}
          {os.arquivada && <Selo tom="neutro">Arquivada</Selo>}
          <Selo tom="neutro">Rev. {String(os.revisao ?? 0)}</Selo>
        </div>
        <p className="cabeca-os-sub">
          {os.cliente}
          {' · '}<span className="mono">{os.obra_codigo}</span> {os.obra_nome}
          {lugar && <> · {lugar}</>}
          {os.prazo_contratual && (
            <>
              {' · prazo '}<span className="mono">{data(os.prazo_contratual)}</span>
              {prazo && (
                <span className={os.atrasada ? 'prazo-atrasado' : undefined}>
                  {' · '}{prazo}
                </span>
              )}
            </>
          )}
        </p>
      </div>
      <div className="cabeca-os-acoes">
        {receber && (
          <Link className="botao secundario receber-da-os" to={`/recebimento/novo?os=${os.id}`}
            title="Registrar um recebimento já com esta OS e as amostras que ela previu">
            Receber amostras
          </Link>
        )}
        {passo && (
          <Link className="botao" to={passo.para} title={passo.diz}>
            {passo.texto}
          </Link>
        )}
        {acoes && <MenuDaOs>{acoes}</MenuDaOs>}
      </div>
    </header>
  );
}

/**
 * O menu «⋯» do cabeçalho da OS (267).
 *
 * O cabeçalho tinha quatro botões ao lado do próximo passo — Campo,
 * Conferir OS, PDF da OS e Excluir de vez — e o próximo passo, que é o
 * que a pessoa veio fazer, competia com eles. Pela ideia do fluxo (§6.1)
 * fica UM botão em destaque; o resto mora aqui.
 *
 * Os itens ficam montados com o menu fechado (`hidden`): o «Conferir OS»
 * abre uma prévia que precisa continuar viva depois que o menu fecha — por
 * isso a prévia vai para o `body` (portal, em `ImprimirOs`).
 */
export function MenuDaOs({ children, rotulo = 'Mais ações da OS' }: {
  children: ReactNode;
  /** O nome do menu para o leitor de tela (a campanha CPTu usa o mesmo menu, onda 312). */
  rotulo?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);
  return (
    <div className="menu-os" ref={raiz}>
      <button type="button" className="botao discreto menu-os-botao"
        aria-haspopup="true" aria-expanded={aberto}
        aria-label={rotulo} title={rotulo}
        onClick={() => setAberto((a) => !a)}>
        <Icone nome="pontos" />
      </button>
      <div className="menu-os-itens" role="group" aria-label={rotulo}
        hidden={!aberto}
        onClick={(e) => {
          /* Escolher um item fecha o menu — a ação continua (link, prévia). */
          if ((e.target as HTMLElement).closest('a, button')) setAberto(false);
        }}>
        {children}
      </div>
    </div>
  );
}

/* ── A régua de etapas ──────────────────────────────────────────────── */

type Estado = 'feito' | 'andamento' | 'pendente' | 'atencao';

/** O símbolo e a palavra de cada estado — nunca só a cor (§18). */
const ESTADO: Record<Estado, { sim: string; palavra: string }> = {
  feito: { sim: '✓', palavra: 'feito' },
  andamento: { sim: '◐', palavra: 'em andamento' },
  pendente: { sim: '○', palavra: 'pendente' },
  atencao: { sim: '▲', palavra: 'atenção' },
};

interface Etapa {
  chave: string; nome: string; conta: string; estado: Estado; aba: AbaDaOs;
}

function estadoDoLaudo(s: string): Estado {
  if (['aprovado', 'emitido', 'disponibilizado'].includes(s)) return 'feito';
  if (s === 'com_pendencia') return 'atencao';
  if (s === 'nao_iniciado') return 'pendente';
  return 'andamento';
}

function estadoDaMedicao(s: string): Estado {
  if (['aprovada', 'emitida'].includes(s)) return 'feito';
  if (s === 'nao_iniciada') return 'pendente';
  if (s.startsWith('aguardando')) return 'atencao';
  return 'andamento';
}

/** As seis etapas da OS, com a conta e o estado de cada uma. */
export function etapasDaOs(os: Os): Etapa[] {
  const n = numerosDaOs(os);
  const rev = Number(os.revisao ?? 0);
  return [
    {
      chave: 'escopo', nome: 'Escopo', aba: 'escopo',
      conta: n.metodos === 0
        ? 'sem ensaio no escopo'
        : `${n.contratados} ensaio(s) · revisão ${rev}`,
      estado: n.metodos === 0 ? 'atencao' : 'feito',
    },
    {
      chave: 'amostras', nome: 'Amostras', aba: 'amostras',
      conta: n.amostras === 0
        ? 'nenhuma ainda'
        : `${n.recebidas} recebida(s) · ${n.porChegar} por chegar`,
      estado: n.amostras === 0 ? 'pendente'
        : n.porChegar === 0 ? 'feito' : 'andamento',
    },
    {
      chave: 'programacao', nome: 'Programação', aba: 'programacao',
      conta: n.contratados > 0
        ? `${n.programadosTodos} de ${plural(n.contratados, 'programado', 'programados')}`
          + (n.reservados > 0 ? ` · ${n.reservados} por chegar` : '')
        : `${n.ensaios} ensaio(s)`,
      estado: n.contratados > 0 && n.programadosTodos >= n.contratados ? 'feito'
        : n.programadosTodos > 0 || n.ensaios > 0 ? 'andamento'
          : os.status_tecnico === 'aguardando_programacao' && n.recebidas > 0
            ? 'atencao' : 'pendente',
    },
    {
      chave: 'execucao', nome: 'Execução', aba: 'resumo',
      conta: n.ensaios === 0
        ? 'nenhum ensaio'
        : n.devolvidos > 0
          ? `${n.devolvidos} devolvido(s) · ${n.aprovados} de ${n.ensaios} aprovado(s)`
          : `${n.aprovados} de ${n.ensaios} aprovado(s)`
            + (n.emExecucao > 0 ? ` · ${n.emExecucao} na bancada` : ''),
      estado: n.ensaios === 0 ? 'pendente'
        : n.devolvidos > 0 ? 'atencao'
          : n.aprovados >= n.ensaios ? 'feito' : 'andamento',
    },
    {
      chave: 'laudo', nome: 'Laudo', aba: 'resumo',
      conta: rotulo(os.status_relatorio).toLowerCase(),
      estado: estadoDoLaudo(os.status_relatorio),
    },
    {
      chave: 'medicao', nome: 'Medição', aba: 'quantitativos',
      conta: rotulo(os.status_medicao).toLowerCase(),
      estado: estadoDaMedicao(os.status_medicao),
    },
  ];
}

/**
 * A régua — «onde se está e o que falta» (§9.1).
 *
 * Seis etapas, sempre as mesmas, na ordem do serviço. Cada uma diz a conta
 * («8 recebida(s) · 2 por chegar») e o estado com símbolo E palavra, e é um
 * link para a aba onde aquilo se resolve. A etapa da aba aberta fica
 * marcada (`aria-current="step"`).
 */
export function ReguaDaOs({ os, aba, semMedicao, semAmostras, semProgramacao }: {
  os: Os; aba: AbaDaOs; semMedicao?: boolean;
  /** H1: a etapa cuja aba some (`AbasDaOs`) também sai da régua — ela é um link para a aba. */
  semAmostras?: boolean; semProgramacao?: boolean;
}) {
  const [aberta, setAberta] = useState(false);
  const etapas = etapasDaOs(os).filter((e) => !(semMedicao && e.chave === 'medicao')
    && !(semAmostras && e.chave === 'amostras')
    && !(semProgramacao && e.chave === 'programacao'));
  const eAtual = (e: Etapa) => e.aba === aba && (aba !== 'resumo' || e.chave === 'execucao');
  /*
   * 282 · no celular a régua vira UMA linha: a etapa desta aba (ou a
   * primeira que ainda anda) por extenso, um trilho de seis traços e
   * «6 etapas ▾» para abrir as seis. «Sim, Proposta topo compacto» —
   * Humberto, 06/10/2026: antes, régua em três linhas e abas em duas
   * comiam ¾ da tela antes do conteúdo. No PC nada muda (o CSS esconde
   * a linha compacta).
   */
  const iAtual = Math.max(0, (() => {
    const i = etapas.findIndex(eAtual);
    if (i >= 0) return i;
    const j = etapas.findIndex((e) => e.estado === 'andamento' || e.estado === 'atencao');
    if (j >= 0) return j;
    const k = etapas.findIndex((e) => e.estado === 'pendente');
    return k >= 0 ? k : etapas.length - 1;
  })());
  const destaque = etapas[iAtual];
  return (
    <div className={`regua-os-caixa${aberta ? ' aberta' : ''}`}>
      {destaque && (
        <div className="regua-os-compacta">
          <div className="regua-os-trilho" aria-hidden="true">
            {etapas.map((e, i) => (
              <span key={e.chave} className={`${e.estado}${i === iAtual ? ' atual' : ''}`} />
            ))}
          </div>
          <div className="regua-os-linha">
            <span className="regua-os-agora">
              <b>{iAtual + 1} · {destaque.nome}</b>
              {' '}
              <span className={`etapa-os-conta ${destaque.estado}`}>
                <span className="etapa-os-sim" aria-hidden="true">{ESTADO[destaque.estado].sim}</span>
                <span className="so-leitor">{ESTADO[destaque.estado].palavra}: </span>
                {destaque.conta}
              </span>
            </span>
            <button type="button" className="regua-os-abrir" aria-expanded={aberta}
              aria-controls={`regua-os-${os.id}`} onClick={() => setAberta((a) => !a)}>
              {etapas.length} etapas {aberta ? '▴' : '▾'}
            </button>
          </div>
        </div>
      )}
      <ol className="regua-os" id={`regua-os-${os.id}`} aria-label="Etapas da OS">
        {etapas.map((e, i) => {
          const atual = eAtual(e);
          const est = ESTADO[e.estado];
          return (
            <li key={e.chave} className={`etapa-os ${e.estado}${atual ? ' atual' : ''}`}>
              <Link to={rotaDaAba(os.id, e.aba)} aria-current={atual ? 'step' : undefined}>
                <span className="etapa-os-nome">{i + 1} · {e.nome}</span>
                <span className="etapa-os-conta">
                  <span className="etapa-os-sim" aria-hidden="true">{est.sim}</span>
                  <span className="so-leitor">{est.palavra}: </span>
                  {e.conta}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ── As abas ────────────────────────────────────────────────────────── */

/**
 * As abas da OS — links, não botões: cada aba tem endereço (§9.1).
 *
 * A contagem ao lado do nome responde «tem alguma coisa aqui?» sem abrir a
 * aba. Desde a 257 a Programação também é desenhada dentro desta moldura
 * (`/os/:id/programacao`).
 *
 * A OS é Comum desde a H1 (onda 295, D1): abre no Hub para quem não tem o
 * LabManager. Amostras (`amostra.ver`) e Programação (`ensaio.ver`) são do
 * LabManager, e a aba some para quem não tem a unidade — como o link do Campo
 * some para quem não tem o Campo (spec 03 §10): a API recusaria o que a aba
 * mostra.
 */
export function AbasDaOs({ os, aba, semQuantitativos, semAmostras, semProgramacao }: {
  os: Os; aba: AbaDaOs; semQuantitativos?: boolean;
  semAmostras?: boolean; semProgramacao?: boolean;
}) {
  const n = numerosDaOs(os);
  /*
   * 282 · no celular as abas ficam numa linha que rola de lado; ao abrir,
   * a aba corrente vem para a vista (a Histórico, a última, não fica
   * escondida à direita). Só mexe na rolagem DA FAIXA — nunca na página.
   */
  const faixa = useRef<HTMLElement>(null);
  const antesDeTrocar = useAbasQueGrudam(faixa, aba);
  useEffect(() => {
    const nav = faixa.current;
    const sel = nav?.querySelector<HTMLElement>('.aba-os.sel');
    if (!nav || !sel || nav.scrollWidth <= nav.clientWidth) return;
    const r = sel.getBoundingClientRect();
    const rn = nav.getBoundingClientRect();
    if (r.left < rn.left || r.right > rn.right) {
      nav.scrollLeft = Math.max(0, nav.scrollLeft + r.left - rn.left - 16);
    }
  }, [aba]);
  /*
   * 304 (RN-249): o Cronograma vem logo depois da Programação — primeiro se
   * marca o ensaio, depois se acompanha. É só leitura e sai da mesma guarda
   * do `GET /os/:id` (`os.ver`): quem vê a OS vê a aba, também no Hub. O
   * contador não é um total — diz «N atrasos» do laboratório, e só quando
   * há; sem atraso, a aba não diz nada (a falta do número É a notícia).
   */
  const atrasos = Number(os.cronograma_atrasos ?? 0);
  const abas: Array<{ aba: AbaDaOs; cont?: number; alerta?: string }> = [
    { aba: 'resumo' },
    { aba: 'escopo', cont: n.metodos },
    ...(semAmostras ? [] : [{ aba: 'amostras' as AbaDaOs, cont: n.amostras }]),
    /* 332 (D11): a aba conta também a marca por chegar — a mesma conta da régua. */
    ...(semProgramacao ? [] : [{ aba: 'programacao' as AbaDaOs, cont: n.ensaios + n.reservados }]),
    {
      aba: 'cronograma',
      alerta: atrasos > 0 ? `${atrasos} ${atrasos === 1 ? 'atraso' : 'atrasos'}` : undefined,
    },
    ...(semQuantitativos ? [] : [{ aba: 'quantitativos' as AbaDaOs }]),
    { aba: 'historico' },
  ];
  return (
    <nav className="abas-os" aria-label="Abas da OS" ref={faixa}>
      {abas.map((a) => (
        <Link key={a.aba} to={rotaDaAba(os.id, a.aba)}
          className={`aba-os${a.aba === aba ? ' sel' : ''}`}
          aria-current={a.aba === aba ? 'page' : undefined}
          onClick={() => antesDeTrocar(a.aba)}>
          {NOME_DA_ABA[a.aba]}
          {a.cont !== undefined && a.cont > 0 && (
            <span className="aba-os-cont">{a.cont}</span>
          )}
          {a.alerta && <span className="aba-os-cont aba-os-atraso">{a.alerta}</span>}
        </Link>
      ))}
    </nav>
  );
}
